package com.mgacreative.hotelru.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.core.network.SupabaseNetworkClient
import com.mgacreative.hotelru.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable

@Serializable
data class Member(
    val id: String = "",
    val email: String = "",
    val full_name: String? = null,
    val member_code: String? = null,
    val role: String? = "MEMBER",
    val phone: String? = null,
    val is_active: Boolean = true,
    val start_date: String? = null,
    val end_date: String? = null,
    val hotel_name: String? = null,
    val tenant_id: String? = null,
    val created_at: String? = null
)

sealed interface MemberListUiState {
    data object Loading : MemberListUiState
    data class Success(
        val members: List<Member>,
        val searchQuery: String = "",
        val selectedStatus: Boolean? = null // null: Tümü, true: Sadece Aktif, false: Sadece Pasif
    ) : MemberListUiState {
        val filteredMembers: List<Member>
            get() = members.filter { member ->
                val query = searchQuery.trim()
                val matchesQuery = query.isBlank() ||
                        (member.full_name?.contains(query, ignoreCase = true) == true) ||
                        member.email.contains(query, ignoreCase = true) ||
                        (member.member_code?.contains(query, ignoreCase = true) == true) ||
                        (member.hotel_name?.contains(query, ignoreCase = true) == true) ||
                        (member.phone?.contains(query, ignoreCase = true) == true)
                val matchesStatus = selectedStatus == null || member.is_active == selectedStatus
                matchesQuery && matchesStatus
            }
    }
    data class Error(val message: String) : MemberListUiState
}

class AdminMembersViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _uiState = MutableStateFlow<MemberListUiState>(MemberListUiState.Loading)
    val uiState: StateFlow<MemberListUiState> = _uiState.asStateFlow()

    private val _actionMessage = MutableStateFlow<String?>(null)
    val actionMessage: StateFlow<String?> = _actionMessage.asStateFlow()

    init {
        loadMembers()
    }

    fun loadMembers() {
        viewModelScope.launch {
            _uiState.value = MemberListUiState.Loading
            try {
                @Serializable
                data class TenantInfo(
                    val id: String,
                    val name: String? = null,
                    val phone: String? = null,
                    val company_title: String? = null
                )

                val tenantsList = try {
                    supabaseClient.client.postgrest["tenants"]
                        .select()
                        .decodeList<TenantInfo>()
                } catch (e: Exception) {
                    emptyList()
                }
                val tenantsMap = tenantsList.associateBy { it.id }

                val usersJson = try {
                    supabaseClient.client.postgrest["users"]
                        .select()
                        .decodeList<kotlinx.serialization.json.JsonObject>()
                } catch (e: Exception) {
                    emptyList()
                }

                val members = usersJson.map { obj ->
                    val id = obj["id"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') } ?: ""
                    val email = obj["email"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') } ?: ""
                    val fullName = obj["full_name"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }
                        ?: email.substringBefore("@")
                    val memberCode = obj["member_code"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }
                        ?: ("MBR-" + if (id.length >= 6) id.take(6).uppercase() else "ADMIN")
                    val isActive = obj["is_active"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.toBooleanStrictOrNull() } ?: true
                    val startDate = obj["start_date"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }
                    val endDate = obj["end_date"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }
                    val tenantId = obj["tenant_id"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }
                    val createdAt = obj["created_at"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }

                    val userHotelName = obj["hotel_name"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }?.takeIf { it.isNotBlank() }
                    val userPhone = obj["phone"]?.let { kotlinx.serialization.json.JsonPrimitive(it.toString()).content.trim('"') }?.takeIf { it.isNotBlank() }

                    val tenant = tenantId?.let { tenantsMap[it] }
                    val realHotelName = userHotelName 
                        ?: tenant?.name?.takeIf { it.isNotBlank() } 
                        ?: tenant?.company_title?.takeIf { it.isNotBlank() }
                        ?: ""
                    val realPhone = userPhone 
                        ?: tenant?.phone?.takeIf { it.isNotBlank() }
                        ?: ""

                    Member(
                        id = id,
                        email = email,
                        full_name = fullName,
                        member_code = memberCode,
                        phone = realPhone,
                        is_active = isActive,
                        start_date = startDate,
                        end_date = endDate,
                        hotel_name = realHotelName,
                        tenant_id = tenantId,
                        created_at = createdAt
                    )
                }

                _uiState.value = MemberListUiState.Success(members = members)
            } catch (e: Exception) {
                _uiState.value = MemberListUiState.Error(e.message ?: "Üyeler yüklenemedi.")
            }
        }
    }

    fun onSearchQueryChange(query: String) {
        val current = _uiState.value as? MemberListUiState.Success ?: return
        _uiState.value = current.copy(searchQuery = query)
    }

    fun onFilterStatusChange(status: Boolean?) {
        val current = _uiState.value as? MemberListUiState.Success ?: return
        _uiState.value = current.copy(selectedStatus = status)
    }

    // 🔄 AKTİF / PASİF DURUMUNU DEĞİŞTİRME (Optimistic Update)
    fun toggleMemberStatus(member: Member) {
        val newStatus = !member.is_active
        val current = _uiState.value as? MemberListUiState.Success ?: return

        viewModelScope.launch {
            // 1. Optimistic UI update (Anında arayüzde göster)
            val updatedList = current.members.map {
                if (it.id == member.id) it.copy(is_active = newStatus) else it
            }
            _uiState.value = current.copy(members = updatedList)

            // 2. Yandex Cloud Backend API / PostgreSQL Güncellemesi
            try {
                @Serializable
                data class StatusUpdate(val is_active: Boolean)

                supabaseClient.client.postgrest["members"].update(
                    StatusUpdate(is_active = newStatus)
                ) {
                    filter { eq("id", member.id) }
                }

                // users tablosunda da varsa güncelle
                try {
                    supabaseClient.client.postgrest["users"].update(
                        StatusUpdate(is_active = newStatus)
                    ) {
                        filter { eq("id", member.id) }
                    }
                } catch (_: Exception) {}

                _actionMessage.value = if (newStatus) {
                    "${member.full_name} hesabı AKTİF yapıldı."
                } else {
                    "${member.full_name} hesabı PASİFE alındı (Girişi kısıtlandı)."
                }
            } catch (e: Exception) {
                // Hata durumunda eski haline döndür
                _uiState.value = current
                _actionMessage.value = "Durum güncellenemedi: ${e.message}"
            }
        }
    }

    // ✏️ ÜYE KODUNU GÜNCELLEME (Member Code Update)
    fun updateMemberCode(member: Member, newCode: String) {
        val cleanCode = newCode.trim().uppercase()
        if (cleanCode.isBlank()) {
            _actionMessage.value = "Üye kodu boş bırakılamaz."
            return
        }
        val current = _uiState.value as? MemberListUiState.Success ?: return

        viewModelScope.launch {
            val updatedList = current.members.map {
                if (it.id == member.id) it.copy(member_code = cleanCode) else it
            }
            _uiState.value = current.copy(members = updatedList)

            try {
                @Serializable
                data class CodeUpdate(val member_code: String)

                supabaseClient.client.postgrest["members"].update(
                    CodeUpdate(member_code = cleanCode)
                ) {
                    filter { eq("id", member.id) }
                }

                try {
                    supabaseClient.client.postgrest["users"].update(
                        CodeUpdate(member_code = cleanCode)
                    ) {
                        filter { eq("id", member.id) }
                    }
                } catch (_: Exception) {}

                _actionMessage.value = "${member.full_name} üye kodu '$cleanCode' olarak güncellendi."
            } catch (e: Exception) {
                _uiState.value = current
                _actionMessage.value = "Üye kodu güncellenemedi: ${e.message}"
            }
        }
    }

    // ✏️ ÜYE TÜM BİLGİLERİNİ GÜNCELLEME (In-Place Row Edit Update)
    fun updateMemberData(updatedMember: Member) {
        val current = _uiState.value as? MemberListUiState.Success ?: return

        viewModelScope.launch {
            val updatedList = current.members.map {
                if (it.id == updatedMember.id) updatedMember else it
            }
            _uiState.value = current.copy(members = updatedList)

            try {
                @Serializable
                data class MemberFullUpdate(
                    val member_code: String,
                    val start_date: String?,
                    val end_date: String?,
                    val hotel_name: String?,
                    val phone: String?,
                    val email: String,
                    val is_active: Boolean
                )

                val payload = MemberFullUpdate(
                    member_code = (updatedMember.member_code ?: "").trim().uppercase(),
                    start_date = updatedMember.start_date?.takeIf { it.isNotBlank() },
                    end_date = updatedMember.end_date?.takeIf { it.isNotBlank() },
                    hotel_name = updatedMember.hotel_name?.takeIf { it.isNotBlank() },
                    phone = updatedMember.phone?.takeIf { it.isNotBlank() },
                    email = updatedMember.email.trim().lowercase(),
                    is_active = updatedMember.is_active
                )

                supabaseClient.client.postgrest["members"].update(payload) {
                    filter { eq("id", updatedMember.id) }
                }

                try {
                    supabaseClient.client.postgrest["users"].update(payload) {
                        filter { eq("id", updatedMember.id) }
                    }
                } catch (_: Exception) {}

                _actionMessage.value = "${updatedMember.email} bilgileri güncellendi."
            } catch (e: Exception) {
                _uiState.value = current
                _actionMessage.value = "Güncelleme başarısız: ${e.message}"
            }
        }
    }

    fun clearActionMessage() {
        _actionMessage.value = null
    }
}
